import React, { createContext, useContext, useState, useCallback } from 'react'
import { getFormattedFilterString, shallowObjectKeysToSnake, formatCandidateDetailsForTracking } from '../utilities'

export const AnalyticsContext = createContext({})

export const AnalyticsConsumer = AnalyticsContext.Consumer

export const AnalyticsProvider = ({ children }) => {
  const [filterLists, setFilterLists] = useState({})
  const [results, setResults] = useState(0)
  const [totalSearches, setTotalSearches] = useState(0)
  const [candidatesList, setCandidatesList] = useState([])

  const trackEvent = ({ action, ...eventProperties }) => {
    const options = {
      integrations: {
        Intercom: false,
      },
    }
    const eventName = 'Company: Candidate Preview List Action'

    const eventPropertiesAsSnakeCase = shallowObjectKeysToSnake(eventProperties)

    window.analytics.track(
      eventName,
      {
        action,
        source: 'demand marketing page',
        category: 'marketing pages',
        label: 'candidate list marketplace preview',
        ...eventPropertiesAsSnakeCase,
      },
      options,
    )
  }

  const addTracking = ({ properties, url, target }) => {
    const onClick = e => {
      trackEvent(properties)
      return true
    }

    return { onClick, onAuxClick: onClick, ...safeHref(url, { target }) }
  }

  const safeHref = (href, options) => {
    const isExternal = href && !href.match(/(^\/.*)|(\.offerzen\.com)/)

    let rel = ''
    if (isExternal) {
      rel += 'noopener'

      if (!options?.allowReferrer) {
        rel += ' noreferrer'
      }
    }

    const hrefProps = { href, rel }

    if (options?.target) {
      hrefProps.target = options.target
    }

    return hrefProps
  }

  const addTrackingForCTA = ({ action, sourceDetail, url }) => {
    const locationsFilter = getFormattedFilterString(filterLists.locations)
    const rolesFilter = getFormattedFilterString(filterLists.roles)
    const skillsFilter = getFormattedFilterString(filterLists.skills)

    const properties = {
      sourceDetail,
      action,
      locationsFilter,
      skillsFilter,
      rolesFilter,
      results,
      totalSearches,
    }

    return addTracking({
      properties,
      url,
    })
  }

  const trackClickCandidateCard = (index, sourceDetail) => {
    const action = 'clicked candidate card'
    const locationsFilter = getFormattedFilterString(filterLists.locations)
    const rolesFilter = getFormattedFilterString(filterLists.roles)
    const skillsFilter = getFormattedFilterString(filterLists.skills)
    const currentIndex = index + 1

    let properties = {
      sourceDetail,
      locationsFilter,
      skillsFilter,
      rolesFilter,
      results,
      totalSearches,
    }

    const currentCandidate = candidatesList.find((candidate, i) => index === i)

    const { title, skills, roles, locations, salaryEu, salaryZa } = formatCandidateDetailsForTracking(currentCandidate)

    properties = {
      ...properties,
      [`profileTitle_${currentIndex}`]: title,
      [`profileSkills_${currentIndex}`]: skills,
      [`rolesAndExperience_${currentIndex}`]: roles,
      [`wantsToWorkIn_${currentIndex}`]: locations,
      [`salaryEurAnnual_${currentIndex}`]: salaryEu,
      [`salaryZarMonthly_${currentIndex}`]: salaryZa,
    }

    return trackEvent({
      action,
      ...properties,
    })
  }

  const trackEventOnSearch = useCallback(
    (action = 'applied search filters') => {
      const locationsFilter = getFormattedFilterString(filterLists.locations)
      const rolesFilter = getFormattedFilterString(filterLists.roles)
      const skillsFilter = getFormattedFilterString(filterLists.skills)
      const searchId = locationsFilter + ', ' + rolesFilter + ', ' + skillsFilter

      let properties = {
        sourceDetail: 'filter search',
        locationsFilter,
        skillsFilter,
        rolesFilter,
        results,
        searchId,
        totalSearches,
      }

      candidatesList.map((candidate, index) => {
        const currentIndex = index + 1
        const rolesAndExperience = []

        candidate.roles.map(role => {
          const experience = role.experience.min + '-' + role.experience.max
          const roleAndExperience = role.title + ' | ' + experience
          rolesAndExperience.push(roleAndExperience)
        })

        const { title, skills, roles, locations, salaryEu, salaryZa } = formatCandidateDetailsForTracking(candidate)

        properties = {
          ...properties,
          [`profileTitle_${currentIndex}`]: title,
          [`profileSkills_${currentIndex}`]: skills,
          [`rolesAndExperience_${currentIndex}`]: roles,
          [`wantsToWorkIn_${currentIndex}`]: locations,
          [`salaryEurAnnual_${currentIndex}`]: salaryEu,
          [`salaryZarMonthly_${currentIndex}`]: salaryZa,
        }

        return properties
      })

      return trackEvent({
        action,
        ...properties,
      })
    },
    [filterLists, candidatesList],
  )

  return (
    <AnalyticsContext.Provider
      value={{
        results,
        setResults,
        totalSearches,
        setTotalSearches,
        addTrackingForCTA,
        setFilterLists,
        setCandidatesList,
        trackEventOnSearch,
        trackClickCandidateCard,
      }}>
      {children}
    </AnalyticsContext.Provider>
  )
}

export const useAnalytics = () => {
  return useContext(AnalyticsContext)
}
